import { isVatRate, type VatRate } from '@/pages/vat-calculator/vat-rates'

export const MAX_VAT_AMOUNT_KOPEKS = 99_999_999_999_999_999n
export const MAX_VAT_INTEGER_DIGITS = 15

export type VatMode = 'add' | 'extract' | 'words'
export type VatCalculationError =
  | 'invalid_input'
  | 'size_limit'
  | 'unsupported_rate'
  | 'calculation_error'

export interface VatAmountLine {
  key: 'net' | 'tax' | 'gross'
  label: string
  kopeks: bigint
  formatted: string
  wording: string
}

export interface VatCalculationResult {
  mode: VatMode
  inputKopeks: bigint
  rate?: VatRate
  lines: VatAmountLine[]
  wording?: string
}

export type VatCalculation =
  | { status: 'success'; result: VatCalculationResult }
  | { status: 'error'; error: VatCalculationError }

export type ParsedVatAmount =
  | { status: 'ready'; kopeks: bigint }
  | { status: 'error'; error: Extract<VatCalculationError, 'invalid_input' | 'size_limit'> }

const AMOUNT_PATTERN =
  /^([0-9]+|[0-9]{1,3}(?:[ \u00A0\u202F][0-9]{3})+)(?:([,.])([0-9]{1,2}))?$/
const GROUPING_SPACES_PATTERN = /[ \u00A0\u202F]/g

export function parseVatAmount(input: string): ParsedVatAmount {
  const trimmed = input.trim()
  if (trimmed === '') return { status: 'error', error: 'invalid_input' }

  const match = AMOUNT_PATTERN.exec(trimmed)
  if (!match) return { status: 'error', error: 'invalid_input' }

  const integerDigits = match[1]!.replace(GROUPING_SPACES_PATTERN, '')
  if (integerDigits.length > MAX_VAT_INTEGER_DIGITS) {
    return { status: 'error', error: 'size_limit' }
  }

  const fractionalDigits = (match[3] ?? '').padEnd(2, '0')
  const kopeks = BigInt(integerDigits) * 100n + BigInt(fractionalDigits || '0')
  if (kopeks > MAX_VAT_AMOUNT_KOPEKS) {
    return { status: 'error', error: 'size_limit' }
  }

  return { status: 'ready', kopeks }
}

export function calculateVat(
  mode: VatMode,
  input: string,
  rateInput: number = 22,
): VatCalculation {
  const parsed = parseVatAmount(input)
  if (parsed.status === 'error') return parsed

  if (mode === 'words') {
    return {
      status: 'success',
      result: {
        mode,
        inputKopeks: parsed.kopeks,
        lines: [],
        wording: amountToRublesWords(parsed.kopeks),
      },
    }
  }

  if (!isVatRate(rateInput)) return { status: 'error', error: 'unsupported_rate' }

  try {
    const rate = BigInt(rateInput)
    const tax = mode === 'add'
      ? roundRationalHalfUp(parsed.kopeks * rate, 100n)
      : roundRationalHalfUp(parsed.kopeks * rate, 100n + rate)
    const net = mode === 'add' ? parsed.kopeks : parsed.kopeks - tax
    const gross = mode === 'add' ? parsed.kopeks + tax : parsed.kopeks

    if ([net, tax, gross].some((value) => value < 0n || value > MAX_VAT_AMOUNT_KOPEKS)) {
      return { status: 'error', error: 'size_limit' }
    }

    return {
      status: 'success',
      result: {
        mode,
        inputKopeks: parsed.kopeks,
        rate: rateInput,
        lines: [
          amountLine('net', 'Сумма без НДС', net),
          amountLine('tax', 'Сумма НДС', tax),
          amountLine('gross', 'Сумма с НДС', gross),
        ],
      },
    }
  } catch {
    return { status: 'error', error: 'calculation_error' }
  }
}

export function roundRationalHalfUp(numerator: bigint, denominator: bigint): bigint {
  if (numerator < 0n || denominator <= 0n) {
    throw new RangeError('Expected a non-negative rational value')
  }
  const quotient = numerator / denominator
  const remainder = numerator % denominator
  return quotient + (remainder * 2n >= denominator ? 1n : 0n)
}

export function formatRubles(kopeks: bigint): string {
  if (kopeks < 0n || kopeks > MAX_VAT_AMOUNT_KOPEKS) {
    throw new RangeError('Amount is outside the supported range')
  }
  const rubles = (kopeks / 100n).toString()
  const grouped: string[] = []
  for (let end = rubles.length; end > 0; end -= 3) {
    grouped.unshift(rubles.slice(Math.max(0, end - 3), end))
  }
  return `${grouped.join(' ')},${(kopeks % 100n).toString().padStart(2, '0')}`
}

export function amountToRublesWords(kopeks: bigint): string {
  if (kopeks < 0n || kopeks > MAX_VAT_AMOUNT_KOPEKS) {
    throw new RangeError('Amount is outside the supported range')
  }

  const rubles = kopeks / 100n
  const kopeckValue = kopeks % 100n
  const words = integerToRussianWords(rubles)
  const rubleForm = chooseForm(Number(rubles % 100n), ['рубль', 'рубля', 'рублей'])
  const kopeckText = kopeckValue.toString().padStart(2, '0')
  const kopeckForm = chooseForm(Number(kopeckValue), ['копейка', 'копейки', 'копеек'])
  const phrase = `${words} ${rubleForm} ${kopeckText} ${kopeckForm}`
  return phrase.charAt(0).toUpperCase() + phrase.slice(1)
}

const HUNDREDS = [
  '',
  'сто',
  'двести',
  'триста',
  'четыреста',
  'пятьсот',
  'шестьсот',
  'семьсот',
  'восемьсот',
  'девятьсот',
] as const
const TENS = ['', '', 'двадцать', 'тридцать', 'сорок', 'пятьдесят', 'шестьдесят', 'семьдесят', 'восемьдесят', 'девяносто'] as const
const TEENS = ['десять', 'одиннадцать', 'двенадцать', 'тринадцать', 'четырнадцать', 'пятнадцать', 'шестнадцать', 'семнадцать', 'восемнадцать', 'девятнадцать'] as const
const UNITS_MALE = ['', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'] as const
const UNITS_FEMALE = ['', 'одна', 'две', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'] as const

const SCALES = [
  null,
  { forms: ['тысяча', 'тысячи', 'тысяч'] as const, feminine: true },
  { forms: ['миллион', 'миллиона', 'миллионов'] as const, feminine: false },
  { forms: ['миллиард', 'миллиарда', 'миллиардов'] as const, feminine: false },
  { forms: ['триллион', 'триллиона', 'триллионов'] as const, feminine: false },
] as const

function integerToRussianWords(value: bigint): string {
  if (value === 0n) return 'ноль'

  const groups: number[] = []
  let remaining = value
  while (remaining > 0n) {
    groups.push(Number(remaining % 1_000n))
    remaining /= 1_000n
  }

  const parts: string[] = []
  for (let index = groups.length - 1; index >= 0; index -= 1) {
    const group = groups[index]!
    if (group === 0) continue
    const scale = SCALES[index]
    parts.push(...tripletWords(group, scale?.feminine ?? false))
    if (scale) parts.push(chooseForm(group, scale.forms))
  }
  return parts.join(' ')
}

function tripletWords(value: number, feminine: boolean): string[] {
  const parts: string[] = []
  const hundreds = Math.floor(value / 100)
  if (hundreds > 0) parts.push(HUNDREDS[hundreds]!)

  const remainder = value % 100
  if (remainder >= 10 && remainder <= 19) {
    parts.push(TEENS[remainder - 10]!)
    return parts
  }

  const tens = Math.floor(remainder / 10)
  const units = remainder % 10
  if (tens > 0) parts.push(TENS[tens]!)
  if (units > 0) parts.push((feminine ? UNITS_FEMALE : UNITS_MALE)[units]!)
  return parts
}

function chooseForm(
  value: number,
  forms: readonly [string, string, string],
): string {
  const normalized = Math.abs(value) % 100
  if (normalized >= 11 && normalized <= 14) return forms[2]
  const lastDigit = normalized % 10
  if (lastDigit === 1) return forms[0]
  if (lastDigit >= 2 && lastDigit <= 4) return forms[1]
  return forms[2]
}

function amountLine(key: VatAmountLine['key'], label: string, kopeks: bigint): VatAmountLine {
  return {
    key,
    label,
    kopeks,
    formatted: formatRubles(kopeks),
    wording: amountToRublesWords(kopeks),
  }
}
